import { getZohoRefreshToken } from "../shared/data/storage/authStorage";
import { getStoredCompanyData, getStoredToken } from "./api";
import { createApiError } from "../utils/errorHandling";
import { GstTreatment } from "../types";
import { API_BASE_URL } from "../config/api";

export interface VendorData {
  vendorid: string;
  zoho_vendorid: string;
  // Vendor Information (Mandatory)
  vendor_legal_name: string;
  vendor_legal_entity: string;
  type_of_vendor: string;
  business_category: string;

  // Optional Vendor Information
  trading_name?: string;
  group_parent_entity?: string;
  vendor_classification?: string;

  // Address Details (Mandatory)
  registered_office_address: string;
  country: string;
  state: string;
  city: string;
  postal_code: string;
  currency_id: string;

  // Optional Address Details
  correspondence_address?: string;
  beneficiary_address?: string;

  // Contact Information (Mandatory)
  primary_contact_name: string;
  primary_contact_phone: string;
  primary_contact_email: string;

  // Optional Contact Information
  alternate_contact?: string;
  escalation_contact?: string;
  website?: string;

  // Compliance & Registration (Mandatory)
  pan_tax_id: string;
  gstin: string;
  msme_flag: string;
  company_registration_number: string;

  // Optional Compliance Fields
  import_export_code?: string;
  iso_certifications?: string;
  regulatory_licenses?: string;
  udyam_number?: string;
  udyam_type?: string;

  // Banking & Payment (Mandatory)
  beneficiary_name: string;
  bank_account_number: string;
  bank_name: string;
  branch: string;
  ifsc_swift_code: string;
  payment_terms: number;
  currency: string;
  currency_symbol?: string;

  // Optional Banking Fields
  preferred_payment_method?: string;
  advance_security_deposit?: number;
  credit_limit?: number;

  // Taxation (Mandatory)
  gst_category: string;
  gst_treatment: GstTreatment;
  tds_applicable: string;

  // Optional Taxation Fields
  party_type?: string;
  tds_tax_id?: number;
  exemption_certificates?: string;
  typical_hsn_sac_codes?: string;

  // Risk & Assessment (Optional)
  risk_rating?: string;

  // Additional Fields (Optional)
  invoice_submission_method?: string;

  // System Fields
  status?: string;
  refresh_token?: string;
}

export interface VendorResponse {
  success: boolean;
  message: string;
  data: VendorData;
  zoho_contact_id: string;
}

// Vendor search interfaces
export interface VendorSearchItem {
  vendorid: string;
  zoho_vendorid: string;
  vendor_legal_name: string;
  vendor_legal_entity: string;
  type_of_vendor: string;
  business_category: string;
  trading_name: string | null;
  group_parent_entity: string | null;
  vendor_classification: string | null;
  registered_office_address: string;
  country: string;
  state: string;
  city: string;
  postal_code: string;
  primary_contact_name: string;
  primary_contact_phone: string;
  primary_contact_email: string;
  correspondence_address: string | null;
  alternate_contact: string | null;
  escalation_contact: string | null;
  website: string | null;
  pan_tax_id: string;
  gstin: string;
  msme_flag: string;
  company_registration_number: string;
  import_export_code: string | null;
  iso_certifications: string | null;
  regulatory_licenses: string | null;
  udyam_number: string | null;
  udyam_type: string | null;
  beneficiary_name: string;
  bank_account_number: string;
  bank_name: string;
  branch: string;
  ifsc_swift_code: string;
  preferred_payment_method: string | null;
  beneficiary_address: string | null;
  advance_security_deposit: number | null;
  withholding_tax_applicability: string;
  withholding_tax_rate: number | null;
  gst_category: string;
  gst_treatment: GstTreatment | null;
  invoice_submission_method: string;
  exemption_certificates: string | null;
  typical_hsn_sac_codes: string | null;
  risk_rating: string;
  payment_terms: string;
  credit_limit: number | null;
  currency: string;
  tds_applicable: string;
  status: string;
  vendor_name_embedding: string | null;
  metadata: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorSearchPagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface VendorSearchResponse {
  success: boolean;
  data: VendorSearchItem[];
  pagination: VendorSearchPagination;
}

export interface VendorSearchParams {
  page?: number; // Deprecated: use offset instead
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  vendor_id?: string;
}

// Detailed vendor response interface for single vendor fetch
export interface VendorDetailResponse {
  success: boolean;
  data: {
    vendorid: number;
    zoho_vendorid: string;
    vendor_legal_name: string;
    vendor_legal_entity: string;
    type_of_vendor: string;
    business_category: string;
    trading_name: string;
    group_parent_entity: string;
    vendor_classification: string;
    registered_office_address: string;
    country: string;
    state: string;
    city: string;
    postal_code: string;
    correspondence_address: string;
    beneficiary_address: string;
    primary_contact_name: string;
    primary_contact_phone: string;
    primary_contact_email: string;
    alternate_contact: string;
    escalation_contact: string;
    website: string;
    pan_tax_id: string;
    gstin: string;
    msme_flag: string;
    company_registration_number: string;
    import_export_code: string;
    iso_certifications: string;
    regulatory_licenses: string;
    udyam_number: string;
    udyam_type: string;
    beneficiary_name: string;
    bank_account_number: string;
    bank_name: string;
    branch: string;
    ifsc_swift_code: string;
    preferred_payment_method: string;
    advance_security_deposit: number;
    payment_terms: number;
    credit_limit: number;
    currency: string;
    withholding_tax_applicability: string;
    withholding_tax_rate: number;
    gst_category: string;
    tds_applicable: string;
    exemption_certificates: string;
    typical_hsn_sac_codes: string;
    risk_rating: string;
    invoice_submission_method: string;
    status: string;
    created_at: string;
    updated_at: string;
  };
}

export const createVendor = async (
  vendorData: VendorData,
): Promise<VendorResponse> => {
  const token = getStoredToken();
  const companyData = getStoredCompanyData();
  vendorData.refresh_token = getZohoRefreshToken() || undefined;
  const response = await fetch(`${API_BASE_URL}/api/bills/vendors`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    // body: JSON.stringify(vendorData),
    body: JSON.stringify({
      vendorData: vendorData,
      company_gst_enabled: companyData.companyGstEnabled,
    }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to create vendor",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export const updateVendor = async (
  vendorId: string,
  vendorData: Partial<VendorData>,
): Promise<VendorResponse> => {
  const token = getStoredToken();
  const companyData = getStoredCompanyData();
  vendorData.refresh_token = getZohoRefreshToken() || undefined;

  const response = await fetch(
    `${API_BASE_URL}/api/bills/vendors/${vendorId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        vendorData: vendorData,
        company_gst_enabled: companyData.companyGstEnabled,
      }),
    },
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to update vendor",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export const searchVendors = async (
  params: VendorSearchParams = {},
): Promise<VendorSearchResponse> => {
  const token = getStoredToken();

  // Build query parameters
  const queryParams = new URLSearchParams();
  // Support both page (for backward compatibility) and offset
  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  } else if (params.page) {
    // Convert page to offset (assuming limit is provided or default 20)
    const limit = params.limit || 20;
    queryParams.append("offset", ((params.page - 1) * limit).toString());
  }
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.status) queryParams.append("status", params.status);
  if (params.vendor_id) queryParams.append("vendor_id", params.vendor_id);

  const url = `${API_BASE_URL}/api/bills/vendors${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to search vendors",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export const getVendorById = async (
  vendorId: string,
): Promise<VendorDetailResponse> => {
  const token = getStoredToken();

  const response = await fetch(
    `${API_BASE_URL}/api/bills/vendors/${vendorId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch vendor details",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

// Vendor list and bills functions for transaction modal
export const getAllVendors = async (params?: {
  search?: string;
  limit?: number;
  offset?: number;
  status?: string;
  country?: string;
  type_of_vendor?: string;
}): Promise<{
  success: boolean;
  data: Array<{
    vendorid: string;
    zoho_vendorid: string;
    vendor_legal_name: string;
    currency_id?: string;
    currency_code?: string;
    currency_symbol?: string;
    gst_treatment?: string;
    gstin?: string;
    country?: string;
    state?: string;
  }>;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}> => {
  const token = getStoredToken();

  // Build query parameters
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.offset) queryParams.append("offset", params.offset.toString());
  if (params?.status) queryParams.append("status", params.status);
  if (params?.country) queryParams.append("country", params.country);
  if (params?.type_of_vendor)
    queryParams.append("type_of_vendor", params.type_of_vendor);

  const url = `${API_BASE_URL}/api/bills/vendors${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch vendors",
      response.status,
      response.statusText,
    );
  }

  const result = await response.json();
  console.log("result", result);
  // Transform the response to match our expected format
  return {
    success: result.success,
    data: result.data.map((vendor: any) => ({
      vendorid: vendor.vendorid,
      zoho_vendorid: vendor.zoho_vendorid,
      vendor_legal_name: vendor.vendor_legal_name,
      currency_id: vendor.currency_id,
      currency_code: vendor.currency_code,
      currency_symbol: vendor.currency_symbol,
      gst_treatment: vendor.gst_treatment,
      gstin: vendor.gstin,
      country: vendor.country,
      state: vendor.state,
    })),
    pagination: result.pagination,
  };
};

export const getBillsForVendor = async (
  vendorZohoId: string,
  limit: number = 20,
  offset: number = 0,
  search?: string,
): Promise<{
  success: boolean;
  data: Array<{
    bill_id: string;
    bill_number?: string;
    vendor_id: string;
    vendor_name: string;
    total: number;
    balance: number;
    tds_total: number;
    currency_code: string;
  }>;
  has_more?: boolean;
}> => {
  const token = getStoredToken();
  const params = new URLSearchParams();
  params.set("vendor_id", vendorZohoId);
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  if (search && search.trim()) params.set("search", search.trim());
  const response = await fetch(
    `${API_BASE_URL}/api/bills/getBillsForVendor?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch vendor bills",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export const getOutstandingBillsForVendor = async (
  vendorZohoId: string,
  limit: number = 20,
  offset: number = 0,
  search?: string,
): Promise<{
  success: boolean;
  data: Array<{
    bill_id: string;
    bill_number: string;
    vendor_id: string;
    vendor_name: string;
    exchange_rate: number;
    currency_id: string;
    total: number;
    balance: number;
    tds_total: number;
    currency_code: string;
    currency_symbol: string;
    status: string;
    due_date: string;
    date: string;
  }>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}> => {
  const token = getStoredToken();

  const params = new URLSearchParams({
    vendor_id: vendorZohoId,
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (search) {
    params.append("search", search);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/bills/getOutstandingBillsForVendor?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch outstanding vendor bills",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export const categorizeVendorTransaction = async (requestBody: {
  // zoho_refresh_token: string;
  transaction_id: string;
  categorize_in_zoho_json: {
    vendor_id: string;
    bills: Array<{
      bill_id: string;
      amount_applied: number;
    }>;
    amount: number;
    paid_through_account_id: string;
  };
}): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  const response = await fetch(
    `${API_BASE_URL}/api/bank-transactions/categorize-vendor`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    },
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to categorize vendor transaction",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export const categorizeMultipleVendorsTransaction = async (requestBody: {
  transaction_id: string;
  vendors: Array<{
    vendor_id: string;
    vendor_name: string;
    bills: Array<{
      bill_id: string;
      bill_number?: string;
      amount_applied: number;
      total?: number;
      balance?: number;
      currency_code?: string;
      currency_symbol?: string;
      status?: string;
    }>;
    advance_payment: number;
    exchange_rate?: number | null;
    bank_charges?: number | null;
  }>;
  date: string;
  account_id: string;
  category_type?: string;
}): Promise<{
  success: boolean;
  message: string;
  data?: any;
}> => {
  const token = getStoredToken();

  const response = await fetch(
    `${API_BASE_URL}/api/bank-transactions/categorize-multiple-vendors`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    },
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to categorize multiple vendors transaction",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export interface ExpenseGlItem {
  gl_id: string;
  gl_name: string;
  account_id: string;
  account_type?: string | null;
}

export const fetchExpenseGls = async (): Promise<{
  success: boolean;
  data: { gls: ExpenseGlItem[] };
  message: string;
}> => {
  const token = getStoredToken();
  const response = await fetch(`${API_BASE_URL}/api/gl-maps/expense-gls`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch expense GLs",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

/**
 * Fetch expense GLs with search and pagination (for SearchableDropdown).
 * Use this for the expense account dropdown; use fetchExpenseGls for legacy "load all" if needed.
 */
export const fetchExpenseGlsPaginated = async (params: {
  search?: string;
  limit: number;
  offset: number;
}): Promise<{
  success: boolean;
  data: {
    gls: ExpenseGlItem[];
    has_more: boolean;
    total: number;
    count?: number;
  };
  message: string;
}> => {
  const token = getStoredToken();
  const searchParams = new URLSearchParams();
  if (params.search != null && params.search !== "") {
    searchParams.set("search", params.search);
  }
  searchParams.set("limit", String(params.limit));
  searchParams.set("offset", String(params.offset));
  const url = `${API_BASE_URL}/api/gl-maps/expense-gls?${searchParams.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }
    throw createApiError(
      errorData.message || "Failed to fetch expense GLs",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};

export const categorizeVendorExpense = async (requestBody: {
  transaction_id: string;
  categorize_in_zoho_json: {
    account_id: string;
    paid_through_account_id: string;
    date: string;
    amount: number;
    notes?: string;
  };
}): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  const response = await fetch(
    `${API_BASE_URL}/api/bank-transactions/categorize-vendor-expense`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    },
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to categorize vendor expense",
      response.status,
      response.statusText,
    );
  }

  return response.json();
};
